import { useEffect, useState } from "react";
import { auth } from "../firebase";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import axios from "axios";

export default function AdminAccessSettings() {
    const [admins, setAdmins] = useState([]);
    const [newAdmin, setNewAdmin] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Fetch admin list
    useEffect(() => {
        const fetchAdmins = async () => {
            setLoading(true);
            setError(null);

            try {
                const user = auth.currentUser;
                if (!user) return;

                const token = await user.getIdToken();
                const res = await axios.get("/api/roles/admins", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                setAdmins(res.data);
            } catch (err) {
                console.error(err);
                setError(err.message || "Error loading admin list");
            } finally {
                setLoading(false);
            }
        };

        fetchAdmins();
    }, []);

    // Add admin
    const handleAdd = async () => {
        if (!newAdmin) return;
        const ok = await confirmSwal({
            title: `Add administrator ${newAdmin.toLowerCase()}?`,
            text: "They will have full access privelages to the Admin Dashboard. Please double check their email address.",
            icon: "danger",
            confirmText: "Make User Admin",
            confirmColor: "#d33",
        });
        if (!ok) return;
        const ok2 = await confirmSwal({
            title: `Are you positive?`,
            text: `${newAdmin.toLowerCase()} will have admin permissions, including the ability to add and remove other admins.`,
            icon: "danger",
            confirmText: "Make User Admin",
            confirmColor: "#d33",
        });
        if (!ok2) return;
        setSaving(true);
        try {
            const user = auth.currentUser;
            const token = await user.getIdToken();

            await axios.post(
                `/api/roles/admins/${encodeURIComponent(newAdmin)}`,
                {}, // No body, so pass an empty object
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setAdmins((prev) => [...prev, newAdmin.toLowerCase()]);
            setNewAdmin("");
            toast.success("Email added as an admin");
        } catch (err) {
            toast.error("Error: " + (err.message || "Failed to add admin"));
        } finally {
            setSaving(false);
        }
    };

    // Remove admin
    const handleRemove = async (email) => {
        const ok = await confirmSwal({
            title: `Remove administrator ${email.toLowerCase()}?`,
            text: "They will lose full access privelages to the Admin Dashboard.",
            icon: "danger",
            confirmText: "Remove Admin",
            confirmColor: "#d33",
        });
        if (!ok) return;
        setSaving(true);
        try {
            const user = auth.currentUser;
            const token = await user.getIdToken();

            await axios.delete(
                `/api/roles/admins/${encodeURIComponent(email)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setAdmins((prev) => prev.filter((e) => e !== email));
            toast.success("Email removed as an admin");
        } catch (err) {
            toast.error("Error: " + (err.message || "Failed to remove admin"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <h5 className="mb-3">Admin Emails</h5>

            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p className="text-danger">Error: {error}</p>
            ) : (
                <>
                    <ul className="list-group mb-4">
                        {admins.map((email) => (
                            <li
                                key={email}
                                className="list-group-item d-flex justify-content-between align-items-center"
                            >
                                {email}
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleRemove(email)}
                                    disabled={saving}
                                >
                                    Remove Admin
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="d-flex gap-2">
                        <input
                            type="email"
                            className="form-control"
                            placeholder="Add admin by email"
                            value={newAdmin}
                            onChange={(e) => setNewAdmin(e.target.value)}
                            disabled={saving}
                        />
                        <button
                            className="btn btn-admin"
                            onClick={handleAdd}
                            disabled={saving}
                        >
                            Add
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
